import { NavigationConfigService } from './config.service';
import { ENVIRONMENT_INITIALIZER, importProvidersFrom, inject, isDevMode, LOCALE_ID } from '@angular/core';
import { DialogModule } from '@angular/cdk/dialog';
import { provideApi, provideSignalRService } from '@cue/api';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { LocaleService } from './locale.service';
import { provideRouter } from '@angular/router';
import { NavigationEnabledGuard } from './navigation-enabled.guard';
import { NotEnabledComponent } from './not-enabled/not-enabled.component';
import { configure, Localization, LOCALIZATIONS, provideLocalization } from '@cue/translate';
import { localizations } from '@cue/translations';
import { TranslocoService } from '@jsverse/transloco';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideGoogleAnalytics } from '@cue/google-analytics';
import { provideCueAnalytics, withHeadersIntecceptor } from '@cue/analytics';
import { provideNavigationSettings } from '@cue/navigation-settings';
import { provideStore } from '@ngrx/store';
import { routerReducer } from '@ngrx/router-store';
import { providePipes } from '@cue/pipes';
import { MessageService } from '@progress/kendo-angular-l10n';
import { ServerNotAliveComponent } from './server-not-alive/server-not-alive.component';
import { provideDeclarativeMapImplementationTypeResolver } from '@cue/map/core';
import { LeafletDeclarativeMapImplementation } from '@cue/map/leaflet';
import { OpenLayersDeclarativeMapImplementation } from '@cue/map/openlayers';

export const appConfig = (configService: NavigationConfigService) => {
  return {
    providers: [
      MessageService,
      {
        provide: NavigationConfigService,
        useValue: configService,
      },
      provideDeclarativeMapImplementationTypeResolver(() => {
        const configService = inject(NavigationConfigService);
        return configService.value.mapEngine === 1
          ? LeafletDeclarativeMapImplementation
          : OpenLayersDeclarativeMapImplementation;
      }),
      provideCueAnalytics(
        withHeadersIntecceptor({
          apiDomains: () => {
            const configService = inject(NavigationConfigService);
            return [configService.value.apiURL];
          },
          deviceIDHeaderName: 'deviceID',
          sessionHeaderName: 'sessionID',
        })
      ),
      provideNavigationSettings({
        configurationFactory: () => {
          return {
            design: configService.value.design,
            url: configService.value.url,
            navigationAppEnabled: null,
            enableReservations: configService.value.enableReservations,
            companyLogoUrl: configService.value.companyLogoUrl,
            deploymentSettings: configService.value.deploymentSettings,
            enableStepNavigation: configService.value.enableStepNavigation,
            gpsInfo: configService.value.gpsInfo,
          };
        },
      }),
      provideGoogleAnalytics({
        keyFactory: () => {
          const configService = inject(NavigationConfigService);
          return configService.value.googleAnalyticsEnabled && configService.value.googleAnalyticsKey != null
            ? configService.value.googleAnalyticsKey
            : undefined;
        },
      }),
      providePipes({
        dateFormat: () => {
          const configService = inject(NavigationConfigService);
          if (configService.value.dateFormat != null) {
            return configService.value.dateFormat;
          } else {
            return undefined;
          }
        },
        timeFormat: () => {
          const configService = inject(NavigationConfigService);
          if (configService.value.timeFormat != null) {
            return configService.value.timeFormat;
          } else {
            return undefined;
          }
        },
      }),
      provideAnimationsAsync(),
      provideHttpClient(
        withInterceptorsFromDi(),
        withInterceptors([
          (req, next) =>
            next(
              req.url.startsWith(configService.value.apiURL)
                ? req.clone({
                    withCredentials: true,
                  })
                : req
            ),
        ])
      ),
      provideStore(
        {
          router: routerReducer,
        },
        {
          runtimeChecks: {
            strictStateImmutability: false,
            strictActionImmutability: false,
          },
        }
      ),
      importProvidersFrom(
        DialogModule,
        ServiceWorkerModule.register('/ngsw-worker.js', {
          enabled: environment.production,
          registrationStrategy: 'registerImmediately',
        })
      ),
      provideLocalization({
        localizations: localizations,
        translocoConfig: {
          availableLangs: configService.value.languages.filter((x) => x.activeInNavigation).map((x) => x.code),
          defaultLang: configService.value.languages.find((x) => x.default).code,
          prodMode: !isDevMode(),
          reRenderOnLangChange: true,
          fallbackLang: configService.value.languages.find((x) => x.default).code,
          missingHandler: {
            useFallbackTranslation: true,
          },
        },
      }),
      provideRouter([
        {
          path: 'dashboard',
          redirectTo: '',
          pathMatch: 'full',
        },
        {
          canActivate: [NavigationEnabledGuard],
          path: '',
          loadChildren: () => import('@cue/navigation-routing').then((x) => x.NavigationsRoutes('./')),
        },
        {
          path: 'not-enabled',
          component: NotEnabledComponent,
        },
        {
          path: 'server-down',
          component: ServerNotAliveComponent,
        },
      ]),
      provideSignalRService({
        // TODO: JAKUB - kde to je?
        factoryClientId: () => 'PEPA',
      }),
      provideApi({
        factory: () => configService.value.apiURL,
      }),
      {
        provide: LOCALE_ID,
        deps: [LocaleService],
        useFactory: (localeService) => localeService.getLocaleId(),
      },
      {
        provide: ENVIRONMENT_INITIALIZER,
        multi: true,
        useValue() {
          const translocoService = inject(TranslocoService);
          const localizations = inject(LOCALIZATIONS) as unknown as Localization[][];
          configure(translocoService, localizations);
        },
      },
    ],
  };
};
